import './ShipmentToolbar.css';
import React, { useEffect, useRef, useState } from 'react';
import { message, Menu } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import FloatingToolbarActions from './FloatingToolbarActions';
import {
  CrossIcon,
  DotsIcon,
  CopyIcon,
  OneTimeArrowIcon,
  ProductsIcon,
  GroupUsersIcon,
  UploadSmallIcon,
} from '@/assets/icons/icons';
import SelectAll from '../../../assets/images/SelectAll.jsx';
import CheckCircle from '../../../assets/images/CheckCircle';
import Archive from '../../../assets/images/Archive';
import ClearAll from '../../../assets/images/ClearAll.jsx';
import NotificationAdd from '../../../assets/images/NotificationAdd.jsx';
import { ExportCOCDropdownWrapper } from '../components/ExportCOCDropdown';
import { CustomTitle } from '@/components';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import { getShipmentToolbarCountText } from './utils';
import { legTypes } from '@/apis/shipment';

interface FloatingToolbarProps {
  checked: boolean;
  setChecked: (checked: boolean) => void;
  selectedCountText: string;
  totalShipments: number;
  appliedFilters: unknown[] | null;
  isSearchTriggered: boolean;
  selected: string[] | null;
  activeTabId?: string;
  getLabel: (count: number) => string;
  handleSelectAll: () => void;
  useSelectAllMode: boolean;
  selectedIds: string[];
  allPermissions?: {
    is_update?: boolean;
    [key: string]: unknown;
  };
  // the "alerts" feature permission, not the shipment one
  alertsPermissions?: {
    is_update?: boolean;
    [key: string]: unknown;
  };
  isAiHistory: boolean;
  itemLabel: string;
  setShowDeliveredModal: (visible: boolean) => void;
  archiveModalVisible: boolean;
  setArchiveModalVisible: (visible: boolean) => void;
  handleClickExportCOC: (exportOptions: unknown) => void;
  fetchCOCCount: () => void;
  cOCExportCounts: {
    selected_shipments?: number;
    status_pdf_count?: number;
    pod_pdf_count?: number;
    shipments_with_pod?: number;
    shipment_ids_with_missing_pod?: string[];
    [key: string]: unknown;
  };
  exportCocDropdownVisible: boolean;
  setExportCocDropdownVisible: (visible: boolean) => void;
  mainMenuVisible: boolean;
  setMainMenuVisible: (visible: boolean) => void;
  handleMissingPODsClick: () => void;
  handleOpenNotification: (type: number) => void;
  handleOpenProductDrawer: () => void;
  setDeleteModalVisible: (visible: boolean) => void;
  setEditTeamsModalVisible?: (visible: boolean) => void;
  // shipment update permission, the same gate as the per-row share action
  canShareTrackingLinks?: boolean;
  handleOpenShareTrackingLinks?: () => void;
  hasValidTeams?: boolean;
  isDrawerOpen?: boolean;
  legType?: legTypes;
}

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  checked,
  setChecked,
  selectedCountText,
  totalShipments,
  appliedFilters,
  isSearchTriggered,
  selected,
  activeTabId,
  getLabel,
  handleSelectAll,
  useSelectAllMode,
  selectedIds,
  allPermissions,
  alertsPermissions,
  isAiHistory,
  itemLabel,
  setShowDeliveredModal,
  setArchiveModalVisible,
  handleClickExportCOC,
  fetchCOCCount,
  cOCExportCounts,
  exportCocDropdownVisible,
  setExportCocDropdownVisible,
  mainMenuVisible,
  setMainMenuVisible,
  handleMissingPODsClick,
  handleOpenNotification,
  handleOpenProductDrawer,
  setDeleteModalVisible,
  setEditTeamsModalVisible,
  canShareTrackingLinks = false,
  handleOpenShareTrackingLinks,
  hasValidTeams = false,
  isDrawerOpen = false,
  legType,
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const actionRefs = useRef<(HTMLElement | null)[]>([]);
  // Focus on close button when toolbar becomes visible
  useEffect(() => {
    if (checked && actionRefs.current[0]) {
      actionRefs.current[0].focus();
      setFocusedIndex(0);
    }
  }, [checked]);

  // Call fetchCOCCount when export dropdown opens to get the counts
  useEffect(() => {
    if (
      exportCocDropdownVisible &&
      (useSelectAllMode || selectedIds.length > 0)
    ) {
      fetchCOCCount();
    }
  }, [
    exportCocDropdownVisible,
    useSelectAllMode,
    selectedIds.length,
    fetchCOCCount,
  ]);

  // Calculate the total number of focusable actions dynamically
  const getTotalActions = () => {
    let total = 2; // Close button + Select All
    if (allPermissions?.is_update) {
      total += 2; // Archive + Mark as Delivered
    }
    total += 1; // More button
    return total;
  };

  // antd only adds its icon spacing class when the icon forwards `className`, and
  // several of these SVG icons don't. The wrapper takes the class instead, so every
  // label starts at the same x regardless of the icon's intrinsic size.
  const menuIcon = (icon: React.ReactNode) => (
    <span className='custom-menu-item-icon'>{icon}</span>
  );

  const allMenuItems = [
    {
      key: '1',
      label: 'Add notification',
      icon: menuIcon(<NotificationAdd color='#fff' />),
      onClick: () => handleOpenNotification(1),
      className: 'custom-menu-item',
    },
    {
      key: '2',
      label: 'One time message',
      icon: menuIcon(<OneTimeArrowIcon fill='#fff' />),
      onClick: () => handleOpenNotification(2),
      className: 'custom-menu-item',
    },
    {
      key: '3',
      label: `Export ${themeT('chainOfCustody', theme.themeType).toLowerCase()}`,
      icon: menuIcon(<CopyIcon color='#fff' />),
      onClick: () => {
        if (!useSelectAllMode && selectedIds.length === 0) {
          message.error(`Please select at least one ${itemLabel}`);
        } else {
          setMainMenuVisible(false);
          setExportCocDropdownVisible(true);
        }
      },
      className: 'custom-menu-item',
    },
    ...(canShareTrackingLinks && handleOpenShareTrackingLinks
      ? [
          {
            key: 'share-tracking-links',
            label: 'Share tracking links',
            icon: menuIcon(
              <UploadSmallIcon color='#fff' width={16} height={16} />
            ),
            onClick: () => {
              setMainMenuVisible(false);
              handleOpenShareTrackingLinks();
            },
            className: 'custom-menu-item',
          },
        ]
      : []),
    {
      key: '4',
      label: 'Product list',
      icon: menuIcon(<ProductsIcon color='#fff' />),
      onClick: () => handleOpenProductDrawer(),
      className: 'custom-menu-item',
    },
  ];

  if (allPermissions?.is_update && hasValidTeams && setEditTeamsModalVisible) {
    allMenuItems.push({
      key: 'edit-teams',
      label: 'Edit teams',
      icon: menuIcon(<GroupUsersIcon color='#fff' />),
      onClick: () => {
        if (!useSelectAllMode && selectedIds.length === 0) {
          message.error(`Please select at least one ${itemLabel}`);
        } else {
          setMainMenuVisible(false);
          setEditTeamsModalVisible(true);
        }
      },
      className: 'custom-menu-item',
    });
  }

  if (alertsPermissions?.is_update) {
    allMenuItems.push({
      key: '5',
      label: 'Dismiss Alerts',
      icon: menuIcon(<ClearAll color='#fff' />),
      onClick: () => {
        if (!useSelectAllMode && selectedIds.length === 0) {
          message.error(
            isAiHistory
              ? 'Please select at least one row to dismiss'
              : 'Please select at least one shipment to dismiss alerts'
          );
        } else {
          setDeleteModalVisible(true);
        }
      },
      className: 'custom-menu-item',
    });
  }

  const menu = (
    <Menu
      className='custom-dropdown-menu'
      selectable={false}
      items={allMenuItems}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setMainMenuVisible(false);
          actionRefs.current[4]?.focus();
        }
      }}
    />
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't handle navigation keys when menu is open - let menu handle its own navigation
    if (mainMenuVisible) {
      return;
    }

    const totalActions = getTotalActions();
    if (!actionRefs.current.length || totalActions === 0) return;

    let newIndex = focusedIndex;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        newIndex = focusedIndex > 0 ? focusedIndex - 1 : totalActions - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        newIndex = focusedIndex < totalActions - 1 ? focusedIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = totalActions - 1;
        break;
      default:
        return;
    }

    setFocusedIndex(newIndex);
    actionRefs.current[newIndex]?.focus();
  };

  const registerActionRef = (index: number) => (el: HTMLElement | null) => {
    actionRefs.current[index] = el;
  };

  return (
    <div
      ref={toolbarRef}
      className={`floating-toolbar-wrapper${isDrawerOpen ? ' drawer-open' : ''}`}
      onKeyDown={handleKeyDown}
    >
      <FloatingToolbarActions
        ref={registerActionRef(0)}
        icon={<CrossIcon color='#fff' />}
        onClick={() => setChecked(!checked)}
        tabIndex={0}
        aria-label='Close toolbar'
      />

      <div className='custom-vertical-divider bg-4b' />
      <CustomTitle
        text={getShipmentToolbarCountText({
          checked,
          selectedCountText,
          totalShipments,
          appliedFilters,
          isSearchTriggered,
          selected,
          activeTabId,
          getLabel,
          includeFilteredEntityLabel: true,
          showActiveQualifier: true,
        })}
        fontSize='12px'
        fontWeight='400'
        lineHeight='20px'
        color='var(--gray-600)'
        marginBottom={0}
        whiteSpace='nowrap'
      />

      <div
        ref={registerActionRef(1)}
        onClick={handleSelectAll}
        className='toolbar-action cursor-pointer'
        tabIndex={0}
        aria-label='Select all shipments'
        role='button'
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectAll();
          }
        }}
      >
        {/* Desktop: text only */}
        <span className='show-on-desktop'>
          <CustomTitle
            text='Select all'
            fontSize='12px'
            fontWeight='600'
            lineHeight='16px'
            color='#fff'
            marginBottom={0}
            whiteSpace='nowrap'
          />
        </span>

        {/* Mobile: icon only */}
        <span className='show-on-mobile'>
          <SelectAll color='#fff' />
        </span>
      </div>

      <div className='custom-vertical-divider bg-4b shipment-toolbar-floating-divider' />

      {allPermissions?.is_update && legType === 'CL' && (
        <>
          <FloatingToolbarActions
            ref={registerActionRef(2)}
            icon={<Archive color='#fff' />}
            label='Archive shipments'
            onClick={() => {
              if (!useSelectAllMode && selectedIds.length === 0) {
                message.error(`Please select at least one ${itemLabel}`);
              } else {
                setArchiveModalVisible(true);
              }
            }}
            tabIndex={0}
            aria-label='Archive shipments'
          />

          <FloatingToolbarActions
            ref={registerActionRef(3)}
            icon={<CheckCircle color='#fff' />}
            label='Mark as delivered'
            onClick={() => {
              if (!useSelectAllMode && selectedIds.length === 0) {
                message.error(`Please select at least one ${itemLabel}`);
              } else {
                setShowDeliveredModal(true);
              }
            }}
            tabIndex={0}
            aria-label='Mark as delivered'
          />
        </>
      )}

      {allPermissions?.is_update && legType === 'EL' && (
        <>
          <FloatingToolbarActions
            ref={registerActionRef(4)}
            icon={<ProductsIcon color='#fff' />}
            label='Product list'
            onClick={() => {
              if (!useSelectAllMode && selectedIds.length === 0) {
                message.error(`Please select at least one ${itemLabel}`);
              } else {
                handleOpenProductDrawer();
              }
            }}
            tabIndex={0}
            aria-label='Mark as delivered'
          />
        </>
      )}

      {legType === 'CL' && (
        <Dropdown
          overlay={menu}
          trigger={['click']}
          placement='top'
          open={mainMenuVisible}
          onOpenChange={(open) => {
            setMainMenuVisible(open);
            if (open) {
              // Focus the first menu item when menu opens
              setTimeout(() => {
                const firstMenuItem = document.querySelector(
                  '.ant-dropdown-menu-item'
                ) as HTMLElement;
                if (firstMenuItem) {
                  firstMenuItem.focus();
                }
              }, 100);
            }
          }}
        >
          <span>
            <button
              ref={registerActionRef(4)}
              className={`more-btn ${mainMenuVisible && 'more-btn-active'}`}
              aria-haspopup='menu'
              aria-expanded={mainMenuVisible}
              tabIndex={0}
              aria-label='More options'
              onClick={() => setMainMenuVisible(true)}
            >
              <DotsIcon color='#fff' />
              <p>More</p>
            </button>
          </span>
        </Dropdown>
      )}

      <ExportCOCDropdownWrapper
        isDetialedView={false}
        fetchCOCCount={fetchCOCCount}
        cOCExportCounts={cOCExportCounts}
        canOpen={() => (useSelectAllMode ? true : selectedIds.length > 0)}
        onExport={(exportOptions: unknown) => {
          handleClickExportCOC(exportOptions);
        }}
        onMissingClick={handleMissingPODsClick}
        onBackClick={() => {
          setExportCocDropdownVisible(false);
          setMainMenuVisible(true);
        }}
        visible={exportCocDropdownVisible}
        onVisibleChange={setExportCocDropdownVisible}
      />
    </div>
  );
};

export default FloatingToolbar;
